import type { TranslationKeys } from '../types';

const zh_TW: TranslationKeys = {
  nav: {
    home: '首頁',
    blockExplorer: '瀏覽器',
    swap: '兌換',
    exchange: '跨鏈兌換',
    docs: '文檔',
    more: '更多',
    close: '關閉',
    bridge: '橋接',
  },
  bridge: {
    title: 'Igra 橋接',
    subtitle: '在 Kaspa 與 Igra（鏈 {chainId}）之間橋接 KAS',
    tabDeposit: '存入 KAS',
    tabDepositSub: 'KAS → iKAS',
    tabWithdraw: '提取 KAS',
    tabWithdrawSub: 'iKAS → KAS',
    chainKaspa: 'Kaspa',
    chainEvm: 'EVM（Igra）',
    useWallet: '使用錢包',
    gate: {
      installTitle: '連接 KasWare 錢包',
      installDesc: '橋接交易完全在 KasWare 內簽署。請安裝擴充功能，或在 KasWare Telegram 應用程式中開啟本頁面。',
      disconnectedTitle: '錢包已中斷連線',
      connectWallet: '連接錢包',
      connectTitle: '連接{chain}',
      connectDesc: '請在 KasWare 中授權{chain}帳戶以繼續。',
      connecting: '連接中…',
      connectChain: '連接{chain}',
    },
    deposit: {
      youSend: '你支付（Kaspa L1）',
      youReceive: '你收到（Igra L2）',
      recipientLabel: '在以下 Igra 位址接收 iKAS',
      fromWallet: '（來自錢包）',
      badAddress: '請輸入有效的 EVM 位址——含大寫字母的位址必須通過 EIP-55 校驗和檢查。',
      connectEvm: '連接 EVM 以自動填入',
      connectingEvm: '正在連接 EVM...',
      infoMin: '最小存入額：{min} KAS。',
      infoMining: '交易 ID 在你的瀏覽器中挖取，需以 97b1 開頭——通常只需幾秒鐘。',
      infoMint: 'Igra 排序器在 Kaspa 上確認 Entry 後鑄造 iKAS。',
      phaseConnecting: '正在連接 Kaspa 網路…',
      phaseFetching: '正在取得你的 UTXO…',
      phaseMining: '正在挖取有效的交易 ID…（已嘗試 {tries} 次）',
      phaseSigning: '等待你在 KasWare 中簽署…',
      phaseBroadcasting: '正在廣播 Entry 交易…',
      cancelMining: '取消挖取',
      successTitle: '存入交易已提交',
      successMined: '嘗試 {tries} 次後找到有效交易 ID（nonce {nonce}）。',
      kaspaTx: 'Kaspa 交易：',
      prefixWarning:
        '廣播後的交易 ID 已不以 97b1 開頭，Igra 可能不會入帳這筆存入——請保留交易 ID 並聯絡支援。',
      newDeposit: '再次存入',
      cta: '橋接 KAS 到 Igra',
      ctaAmount: '橋接 {amount} KAS 到 Igra',
    },
    withdraw: {
      youBurn: '你銷毀（Igra L2）',
      paidOut: '放款至（Kaspa L1）',
      payoutLabel: 'KAS 收款位址',
      infoLimits: '限額：每筆提取 {min} – {max} iKAS。',
      infoFeeZero: '協議費用：目前為 0。',
      infoFeeQuoted: '協議費用：請求時報價。',
      infoRate: '1 iKAS 銷毀後精確放款 1 KAS（無點差）。',
      warningPrefix: '提現',
      warningStrong: '並非即時到帳',
      warningRest:
        'iKAS 將在 Igra 上銷毀，隨後由多簽委員會審核請求並在 Kaspa 上釋放 KAS。請只傳送你可以等待的金額。',
      checking: '檢查中…',
      modalTitle: '確認提現',
      modalBurn: '你銷毀',
      modalFee: '協議費用',
      modalPayout: 'KAS 放款至',
      modalAck: '我已知悉放款由多簽委員會人工審核，並非即時到帳。',
      modalConfirm: '確認銷毀 iKAS',
      modalBack: '返回',
      successTitle: '提現請求已在 Igra 上確認',
      requestId: '提現請求 ID：{id}',
      igraTx: 'Igra 交易：',
      successPayout: 'KAS 將發送至：',
      successNote: '多簽審核完成後，KAS 將放款至你的收款位址。',
      newWithdrawal: '再次提取',
      requesting: '正在請求提現…',
      cta: '銷毀 iKAS 並請求 KAS',
      ctaAmount: '銷毀 {amount} iKAS 並請求 KAS',
    },
  },
  home: {
    tagline: 'KasWare 錢包',
    hero: {
      subtitle: '一個自託管錢包，同時管理 TON、Kaspa 與 EVM 資產——私鑰始終留在你的裝置上。',
      chainsLabel: '已支援網路',
      ctaDownload: '加入 Chrome',
      ctaTelegram: '在 Telegram 中開啟',
      note: '完全免費，免註冊，不需電子郵件。',
    },
    pillars: {
      security: {
        title: '安全從設計開始',
        desc: '私鑰在本機產生並加密保存。每筆交易簽名前都會經過風險檢查，並完整顯示交易內容。',
      },
      convenience: {
        title: '一個錢包就夠',
        desc: '在 TON、Kaspa 與 EVM 之間收發、兌換、跨鏈，不必再切換多個錢包與帳戶。',
      },
      friendly: {
        title: '為真實的人設計',
        desc: '白話提示、清楚的地址顯示、深淺色主題，並內建七種語言。',
      },
      reliable: {
        title: '值得依賴',
        desc: '交易紀錄公開可查，多節點線路自動切換，單一服務異常時錢包依然可用。',
      },
    },
    content: {
      downloadTitle: '下載',
      securityTitle: '資產為什麼始終屬於你',
      p1: 'KasWare 是一款支援 Ton、Kaspa 和 EVM 的自託管錢包。',
      p2: '我們無法存取您的資金。您的私鑰由密碼加密儲存於本機裝置，不會與任何人共享。',
      p3: '所有帳戶均由您的助記詞確定性派生，確保您擁有完全的所有權和控制權。',
    },
  },
  about: {
    contact: '聯絡我們',
    contacts: {
      twitter: 'Twitter',
      telegram: 'Telegram',
      discord: 'Discord',
      email: '電子郵件',
    },
  },
  footer: {
    privacy: '隱私政策',
    copyright: '© {year} Flying Rabbit Tech Ltd 版權所有',
  },
  explorer: {
    title: '交易瀏覽器',
    subtitle: '按地址搜尋 Kaspa 和 KRC-20 交易記錄',
    timeRange: '時間範圍：',
    to: '至',
    clear: '清除',
    address: '地址：',
    kasTab: 'KAS 轉帳',
    krc20Tab: 'KRC-20 操作',
    enterAddress: '輸入 Kaspa 地址以查看交易記錄',
    loading: '載入中...',
    search: {
      placeholder: '輸入 Kaspa 地址 (kaspa:q...)',
      button: '搜尋',
    },
    tx: {
      loading: '正在載入交易...',
      empty: '該地址未找到交易記錄',
      emptyInRange: '在選定時間範圍內未找到該地址的交易記錄',
      failed: '載入交易失敗：{error}',
      retry: '重試',
    },
    krc20: {
      loading: '正在載入 KRC-20 操作...',
      empty: '該地址未找到 KRC-20 操作',
      emptyInRange: '在選定時間範圍內未找到該地址的 KRC-20 操作',
      failed: '載入 KRC-20 操作失敗：{error}',
      retry: '重試',
    },
  },
  languageNames: {
    en: 'EN',
    id: 'ID',
    fr: 'FR',
    zh_CN: '简体中文',
    zh_TW: '繁體中文',
    es: 'ES',
    th: 'TH',
  },
};

export default zh_TW;
